/** What a draft will submit, read for the review page: the decisions moved (with who set them), the "why" of each family, the cards answered and the call. */
import type { ContentCatalog, DraftView, TeamState } from "@bsim/contracts";
import { callRequiredIn, type Decisions, type DecisionFamily } from "@bsim/domain";
import { formatKpi } from "../../format";
import { quotedTitle } from "../../format/labels";
import { bandLabel, CALL_NAME } from "../guided/guided";
import { deskLabel, deskOrder, OBJECTIVE_LABELS } from "../labels";
import { isOneRound } from "../deskSheet";
import { decisionSource, setBy } from "../forced";
import { changeLegs, decisionTitle, oneRoundLine } from "../decisionValues";
import { planEveOf, riskProjectionOf } from "../../api/riskFields";
import { ownLimitsLine, type RiskyChoices } from "../../events/ownLimits";
import { answeredWith } from "../../events/deck";

export interface PlanSummary {
  readonly decisions: readonly { readonly id: string; /** The leaf path of the moved leg (the trace names its rows by it). */ readonly path: string; readonly oneLeg: boolean; readonly title: string; readonly change: string; readonly source: string | null; /** A card answer holds it (provenance), not the regulator or the standing policy: its row is the plan's starting point. */ readonly cardSet: boolean }[];
  readonly rationales: readonly { readonly family: DecisionFamily; readonly label: string; readonly aim: string; readonly text: string }[];
  readonly cards: readonly string[];
  /** A limit of the player's own that an answered card's option breaks (the card is its cause), repeated so review does not hide it. */
  readonly cardBreaches: readonly string[];
  /** The call, written; null before it is committed or in a round with no call. */
  readonly call: string | null;
  readonly thesis: string | null;
}

const callText = (team: TeamState): string | null => {
  const f = team.forecast;
  if (f === null || f.state !== "committed" || f.forecast === null) return null;
  const c = f.forecast;
  return c.format === "numeric"
    ? `${CALL_NAME.nim} ${formatKpi("nim", c.nim)} · ${CALL_NAME.ncoRate} ${formatKpi("ncoRate", c.ncoRate)} · ${CALL_NAME.cet1Marked} ${formatKpi("cet1Marked", c.cet1Marked)}`
    : `${CALL_NAME.nim} ${bandLabel(c.nim, f.bands?.nim)} · ${CALL_NAME.ncoRate} ${bandLabel(c.ncoRate, f.bands?.ncoRate)} · ${CALL_NAME.cet1Marked} ${bandLabel(c.cet1Marked, f.bands?.cet1Marked)}`;
};

export function planSummary(team: TeamState, draft: DraftView, catalog: ContentCatalog, seed: Partial<Decisions>, risks: RiskyChoices = []): PlanSummary {
  const decisions = catalog.decisions.flatMap((entry) => {
    const title = decisionTitle(catalog.decisions, entry.decisionId);
    const legs = changeLegs(entry, draft.decisions, seed);
    return legs.map(({ leg, sub, change }) => ({ id: entry.decisionId, path: sub === "" ? entry.decisionId : `${entry.decisionId}.${sub}`, oneLeg: Object.keys(entry.options).length === 1, title: leg === null || legs.length === 1 ? title : `${title}, ${leg.toLowerCase()}`, change: isOneRound(entry) ? `${change} (${oneRoundLine(entry)})` : change, source: decisionSource(entry, draft, team), cardSet: setBy(entry, draft, team)?.source === "card" }));
  });
  const rationales = deskOrder(catalog).flatMap((family) => {
    const r = draft.rationales[family];
    return r === undefined || !draft.changedFamilies.includes(family) ? [] : [{ family, label: deskLabel(catalog, family), aim: OBJECTIVE_LABELS[r.objective], text: r.text }];
  });
  const cards = team.pendingEvents.flatMap((e) => {
    const answer = answeredWith(e);
    return answer === undefined ? [] : [`${quotedTitle(e.card.title)} · ${answer.label}`];
  });
  const cardBreaches = team.pendingEvents.flatMap((e) => {
    const choice = answeredWith(e);
    const risk = choice === undefined ? undefined : risks.find((r) => r.instanceId === e.eventInstanceId && r.choiceId === choice.id);
    const line = risk === undefined ? null : ownLimitsLine(risk, planEveOf(riskProjectionOf(draft)));
    return choice === undefined || line === null || !line.breached ? [] : [`${quotedTitle(e.card.title)} · ${choice.label}: ${line.text.replace(/^Your limits: /, "breaks your limit: ")}`];
  });
  return { decisions, rationales, cards, cardBreaches, call: callRequiredIn(team.round?.roundNo ?? 0) ? callText(team) : null, thesis: draft.thesis.text };
}
